import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { sortByName } from "@/utils/sortFunctions";
import { SET_FOCUSED_PROJECT } from "@/store/sideNav/sideNavActions";
import { doRedirect } from "@/store/metadata/metadataActions";
import { projectItemSx, railSectionSx } from "../styles";

interface UserProjectsProps {
  isUsersProfile: boolean;
}

interface UserProject {
  id: number;
  name: string;
}

const UserProjects = ({ isUsersProfile }: UserProjectsProps) => {
  const { t } = useTranslation(["translation.Profile"]);
  const dispatch = useDispatch();

  const projects: UserProject[] = useSelector(
    (state: any) =>
      isUsersProfile
        ? state.project.toJS()
        : state.browse.browseProfile.get("projects").toJS(),
    shallowEqual
  );
  const projectsHaveLoaded = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("projectsLoaded"))
      : true
  );

  const clickProject = (projectId: number) => {
    dispatch({ type: SET_FOCUSED_PROJECT, projectId });
    dispatch(doRedirect(`/project/${projectId}`));
  };

  const heading = (title: string) => (
    <Typography variant="h3" sx={{ color: "common.white", mb: 1 }}>
      {title}
    </Typography>
  );

  if (!projectsHaveLoaded) {
    return (
      <Box sx={railSectionSx}>
        {heading(t("Projects"))}
        <Typography variant="body2" sx={{ color: "grey.400" }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box sx={railSectionSx}>
        {heading(t("Projects"))}
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          <Typography variant="body2" sx={{ color: "common.white" }}>
            {isUsersProfile ? t("Boohoo") : t("NoProjects")}
          </Typography>
          {isUsersProfile && (
            <Button
              component={Link}
              to="/project/create-project"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              sx={{ ...projectItemSx, minWidth: 0, textTransform: "none" }}
            >
              {t("CreateProject")}
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={railSectionSx}>
      {heading(`${t("Project")}(s)`)}
      <List disablePadding>
        {sortByName(projects).map((project: UserProject) => (
          <ListItemButton
            key={`${project.id}-${project.name}`}
            onClick={() => clickProject(project.id)}
            sx={projectItemSx}
          >
            <ListItemText
              primary={project.name}
              slotProps={{ primary: { variant: "body2" } }}
            />
          </ListItemButton>
        ))}
      </List>

      {isUsersProfile && (
        <Button
          component={Link}
          to="/project/create-project"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          sx={{ ...projectItemSx, minWidth: 0, textTransform: "none", mt: 1 }}
        >
          {t("CreateNewProject")}
        </Button>
      )}
    </Box>
  );
};

export default UserProjects;
